import React from 'react';
import PropTypes from 'prop-types';
import LoadingSpinner from '../../Global/LoadingSpinner/LoadingSpinner';
import ErrorComponent from '../ErrorComponent/ErrorComponent';

/**
 * LoadingContainer - Wrapper component for handling loading/error/empty states
 * 
 * Displays appropriate UI based on loading state:
 * - LoadingSpinner while loading
 * - ErrorComponent on error
 * - Empty state when no data
 * - Children when data is ready
 * 
 * @example
 * <LoadingContainer
 *   isLoading={isLoading}
 *   error={error}
 *   isEmpty={products.length === 0}
 *   emptyMessage="No products found"
 * >
 *   <ProductList products={products} />
 * </LoadingContainer>
 */
function LoadingContainer({
  isLoading,
  error,
  isEmpty,
  children,
  loadingMessage,
  emptyMessage = 'No data available',
  emptyComponent: EmptyComponent,
  componentName,
  spinnerClassName = 'lds-default lds-blue',
  showAlert = false,
  wrapperClassName = '',
  overlay = false,
}) {
  // Loading state
  if (isLoading) {
    const spinner = (
      <LoadingSpinner
        className={spinnerClassName}
        showAlert={showAlert}
        alertMessage={loadingMessage}
      />
    );

    // Overlay mode (for overlaying existing content)
    if (overlay) {
      return (
        <>
          {children}
          <div className={`loading-overlay ${wrapperClassName}`}>
            <div className="loading-overlay__background"></div>
            <div className="loading-overlay__spinner">{spinner}</div>
          </div>
        </>
      );
    }

    // Standard mode (replaces content)
    return <div className={wrapperClassName}>{spinner}</div>;
  }

  // Error state
  if (error) {
    return (
      <div className={wrapperClassName}>
        <ErrorComponent error={error} componentName={componentName} />
      </div>
    );
  }

  // Empty state
  if (isEmpty) {
    if (EmptyComponent) {
      return (
        <div className={wrapperClassName}>
          <EmptyComponent />
        </div>
      );
    }

    return (
      <div className={`empty-state ${wrapperClassName}`}>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Success state - render children
  return <>{children}</>;
}

LoadingContainer.propTypes = {
  /** Whether content is currently loading */
  isLoading: PropTypes.bool.isRequired,
  
  /** Error object if an error occurred */
  error: PropTypes.oneOfType([
    PropTypes.instanceOf(Error),
    PropTypes.object,
    PropTypes.string,
  ]),
  
  /** Whether the data is empty (e.g., empty array) */
  isEmpty: PropTypes.bool,
  
  /** Content to render when loaded successfully */
  children: PropTypes.node,
  
  /** Message to show while loading (for screen readers) */
  loadingMessage: PropTypes.string,
  
  /** Message to show when data is empty */
  emptyMessage: PropTypes.string,
  
  /** Custom component to render for empty state */
  emptyComponent: PropTypes.elementType,
  
  /** Name of component for error reporting */
  componentName: PropTypes.string,
  
  /** CSS class for LoadingSpinner */
  spinnerClassName: PropTypes.string,
  
  /** Whether to show ARIA alert for loading message */
  showAlert: PropTypes.bool,
  
  /** CSS class for wrapper div */
  wrapperClassName: PropTypes.string,
  
  /** Whether to overlay loading spinner on existing content */
  overlay: PropTypes.bool,
};

LoadingContainer.defaultProps = {
  error: null,
  isEmpty: false,
  loadingMessage: 'Loading...',
  emptyMessage: 'No data available',
  emptyComponent: null,
  componentName: 'Component',
  spinnerClassName: 'lds-default lds-blue',
  showAlert: false,
  wrapperClassName: '',
  overlay: false,
};

export default LoadingContainer;
